import moment from 'moment';
import { StripeSubscriptionState } from '../../pages/Onboarding/Subscription/SubscriptionMetadata';

const NEW_CLIENTS_FOR_N_DAYS = 7; // Counts as a new client for N days after service is started.

const ClientStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  ON_BREAK: 'ON_BREAK',
  DELINQUENT: 'DELINQUENT',
  CANCELED: 'CANCELED',
  PAST_DUE: 'PAST_DUE',
  FUTURE_START: 'FUTURE_START',
  INCOMPLETE: 'INCOMPLETE',
  FUTURE_CANCELLATION: 'FUTURE_CANCELLATION',
  AMENDMENT_ACCEPTED: 'AMENDMENT_ACCEPTED',
  MONTHLY_START: 'MONTHLY_START',
};

const ActiveSubscriptionClientStatuses = [
  ClientStatus.ACTIVE,
  ClientStatus.ON_BREAK,
  ClientStatus.FUTURE_START,
  ClientStatus.FUTURE_CANCELLATION,
  ClientStatus.AMENDMENT_ACCEPTED,
  ClientStatus.MONTHLY_START,
];

const ClientExcludingFilters = {
  NEW_CLIENT: 'NEW_CLIENT',
  AI_FRIEND: 'AI_FRIEND',
};

const clientStatusLabel = {
  [ClientStatus.ACTIVE]: 'Active',
  [ClientStatus.INACTIVE]: 'Inactive',
  [ClientStatus.ON_BREAK]: 'On a break',
  [ClientStatus.DELINQUENT]: 'Payment failed',
  [ClientStatus.CANCELED]: 'Canceled',
  [ClientStatus.PAST_DUE]: 'Past due',
  [ClientStatus.FUTURE_START]: 'Future start',
  [ClientStatus.INCOMPLETE]: 'Incomplete subscription',
  [ClientStatus.FUTURE_CANCELLATION]: 'Future cancellation',
  [ClientStatus.AMENDMENT_ACCEPTED]: 'Amending terms',
  [ClientStatus.MONTHLY_START]: 'Monthly start',
};

const isFutureStart = (user) => user.serviceStartAt
  && moment(user.serviceStartAt).isAfter(moment());
const isOnBreak = (user) => !!user.isActive && !isFutureStart(user) && moment(user.breakEndDate).isAfter(moment());

const filterChecks = {
  [ClientStatus.ACTIVE]: (user) => !!user.isActive && !isOnBreak(user) && !isFutureStart(user),
  [ClientStatus.INACTIVE]: (user) => !user.isActive,
  [ClientStatus.ON_BREAK]: isOnBreak,
  [ClientStatus.DELINQUENT]: (user) => !!user.isDelinquent,
  [ClientStatus.CANCELED]: (user) => user.subscriptionStatus?.toLowerCase() === StripeSubscriptionState.CANCELED,
  [ClientStatus.PAST_DUE]: (user) => user.subscriptionStatus?.toLowerCase() === StripeSubscriptionState.PAST_DUE,
  [ClientStatus.FUTURE_START]: isFutureStart,
  [ClientStatus.INCOMPLETE]: (user) => [StripeSubscriptionState.INCOMPLETE, StripeSubscriptionState.INCOMPLETE_EXPIRED]
    .includes(user.subscriptionStatus?.toLowerCase()),
  [ClientStatus.MONTHLY_START]: (user) => !!user.monthlyStartAt
    && user.subscriptionStatus?.toLowerCase() !== StripeSubscriptionState.CANCELED
    && moment.unix(user.monthlyStartAt).isAfter(moment())
    && moment.unix(user.monthlyStartAt).isBefore(moment().add(1, 'month')),
  [ClientStatus.AMENDMENT_ACCEPTED]: (user) => !!user.amendingAt
    && moment.unix(user.amendingAt).isBefore(moment().add(1, 'month')),
  [ClientStatus.FUTURE_CANCELLATION]: (user) => !!user.cancelAt
    && user.subscriptionStatus?.toLowerCase() !== StripeSubscriptionState.CANCELED
    && moment.unix(user.cancelAt).isAfter(moment()),
};

// Clients on products that skip the post payment form never get a
// postPaymentFormSubmittedAt — the requirement is satisfied by product config,
// so any status logic keying on the form timestamp must go through this helper,
// except where explicitly noted (see isNewClient below), or skip-product clients
// look forever-pending.
const hasSatisfiedPostPaymentForm = (user) => !!user.postPaymentFormSubmittedAt || !!user.productSkipsPPF;

// Returns true if the user is within the new-client badge window and has not been re-onboarded
// or had their contract amended. Re-onboarded/amended clients have a serviceStartAt that pre-dates
// their postPaymentFormSubmittedAt (service was already running when the form was re-submitted).
// Skip-PPF clients have no form timestamp, so for them the window alone decides.
const isNewClient = (user) => {
  if (!user.serviceStartAt) return false;
  // Deliberately NOT wired through hasSatisfiedPostPaymentForm: the re-onboarding check below is
  // date math against an actual submission timestamp, which skip-PPF clients never have. The raw
  // existence check already does the right thing for them (no timestamp -> original onboarding
  // -> keep the New badge for the window), so the helper would add nothing here but a false
  // impression that skip-PPF is handled through it.
  const isOriginalOnboarding = !user.postPaymentFormSubmittedAt
    || !moment(user.serviceStartAt).isBefore(moment(user.postPaymentFormSubmittedAt));
  return isOriginalOnboarding && moment().isBefore(moment(user.serviceStartAt).add(NEW_CLIENTS_FOR_N_DAYS, 'days'));
};

const filterExcludingChecks = {
  [ClientExcludingFilters.NEW_CLIENT]: (user) => isNewClient(user),
};

/**
 * Is eligible for an amendment to the active subscription.
 * Rules are:
 * - Client is active
 * - Client is not on a break
 * - Client is not future start
 */
const isEligibleForActiveSubscriptionAmendment = (user) => (
  filterChecks[ClientStatus.ACTIVE](user)
  && !filterChecks[ClientStatus.FUTURE_START](user)
  && !filterChecks[ClientStatus.ON_BREAK](user)
);

// A one-time payer is a client who paid via Stripe but has no subscription -
// their invoice history is the only record of payment.
const isOneTimePayer = (subscriptionData, invoiceList) => (
  !subscriptionData && !!invoiceList?.length
);

export {
  clientStatusLabel,
  ClientStatus,
  ClientExcludingFilters,
  filterChecks,
  filterExcludingChecks,
  NEW_CLIENTS_FOR_N_DAYS,
  ActiveSubscriptionClientStatuses,
  isEligibleForActiveSubscriptionAmendment,
  hasSatisfiedPostPaymentForm,
  isNewClient,
  isOneTimePayer,
};
